import {Bell, Settings as SettingsIcon, Shield, User} from 'lucide-react';
import {Avatar, Box, Button, Card, CardContent, Grid, MenuItem, Stack, Switch, TextField, Typography} from '@mui/material';
import {useMemo} from 'react';
import {PortalShell} from '../../shared/ui';
import {useAuth} from '../../shared/auth/AuthContext';

export default function ConfiguracoesPage() {
    const {userLogged} = useAuth();
    const initials = useMemo(() => (userLogged?.name || userLogged?.username || 'BF').split(' ').slice(0, 2).map((part) => part[0]).join('').toUpperCase(), [userLogged]);
    const companyName = userLogged?.companyName || userLogged?.empresa?.nome_fantasia || 'BusiFarm';

    return (
        <PortalShell title="Configurações" subtitle="Perfil, segurança e preferências">
            <Stack spacing={3} sx={{maxWidth: 960}}>
                <Box>
                    <Typography variant="h4" sx={{mb: 1}}>Configurações</Typography>
                    <Typography color="text.secondary">Gerencie suas preferências e informações da conta.</Typography>
                </Box>

                <Card>
                    <CardContent sx={{p: 3.5}}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                            <Box sx={{p: 1.25, borderRadius: '14px', bgcolor: 'rgba(37,99,235,0.10)', color: 'primary.main'}}><User size={20} /></Box>
                            <Box>
                                <Typography variant="h6">Perfil</Typography>
                                <Typography variant="body2" color="text.secondary">Atualize suas informações pessoais</Typography>
                            </Box>
                        </Stack>
                        <Stack direction={{xs: 'column', md: 'row'}} spacing={3} alignItems={{xs: 'flex-start', md: 'center'}} sx={{mb: 3}}>
                            <Avatar sx={{width: 80, height: 80, bgcolor: 'primary.main', fontSize: 28, fontWeight: 700}}>{initials}</Avatar>
                            <Button variant="outlined">Alterar foto</Button>
                        </Stack>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}><TextField label="Nome" defaultValue={userLogged?.name || userLogged?.username || ''} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="E-mail" defaultValue={userLogged?.email || ''} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="Telefone" defaultValue={userLogged?.phone || ''} /></Grid>
                            <Grid item xs={12} md={6}><TextField label="Empresa" defaultValue={companyName} disabled /></Grid>
                        </Grid>
                        <Stack direction="row" justifyContent="flex-end" sx={{mt: 3}}><Button variant="contained">Salvar alterações</Button></Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{p: 3.5}}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                            <Box sx={{p: 1.25, borderRadius: '14px', bgcolor: 'rgba(37,99,235,0.10)', color: 'primary.main'}}><Shield size={20} /></Box>
                            <Box>
                                <Typography variant="h6">Segurança</Typography>
                                <Typography variant="body2" color="text.secondary">Gerencie senha e autenticação</Typography>
                            </Box>
                        </Stack>
                        <Stack spacing={2}>
                            <TextField label="Senha atual" type="password" placeholder="••••••••" />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}><TextField label="Nova senha" type="password" placeholder="••••••••" /></Grid>
                                <Grid item xs={12} md={6}><TextField label="Confirmar senha" type="password" placeholder="••••••••" /></Grid>
                            </Grid>
                            <Box sx={{pt: 2, borderTop: '1px solid', borderColor: 'divider'}}>
                                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" spacing={2} alignItems={{sm: 'center'}}>
                                    <Box>
                                        <Typography sx={{fontWeight: 600}}>Autenticação de dois fatores (MFA)</Typography>
                                        <Typography variant="body2" color="text.secondary">Adicione uma camada extra de segurança</Typography>
                                    </Box>
                                    <Button variant="outlined">Ativar MFA</Button>
                                </Stack>
                            </Box>
                            <Stack direction="row" justifyContent="flex-end"><Button variant="contained">Atualizar senha</Button></Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{p: 3.5}}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                            <Box sx={{p: 1.25, borderRadius: '14px', bgcolor: 'rgba(37,99,235,0.10)', color: 'primary.main'}}><Bell size={20} /></Box>
                            <Box>
                                <Typography variant="h6">Notificações</Typography>
                                <Typography variant="body2" color="text.secondary">Escolha como deseja receber notificações</Typography>
                            </Box>
                        </Stack>
                        <Stack spacing={1}>
                            {[
                                {title: 'E-mail', description: 'Receber notificações por e-mail'},
                                {title: 'Financeiro', description: 'Faturas, pagamentos e cobranças'},
                                {title: 'Sistemas', description: 'Atualizações e manutenções'},
                                {title: 'Suporte', description: 'Atualizações de chamados'},
                            ].map((item, index) => (
                                <Stack key={item.title} direction="row" justifyContent="space-between" alignItems="center" sx={{py: 1.75, borderBottom: index === 3 ? 'none' : '1px solid', borderColor: 'divider'}}>
                                    <Box>
                                        <Typography sx={{fontWeight: 600}}>{item.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">{item.description}</Typography>
                                    </Box>
                                    <Switch defaultChecked={index !== 0} />
                                </Stack>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent sx={{p: 3.5}}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 3}}>
                            <Box sx={{p: 1.25, borderRadius: '14px', bgcolor: 'rgba(37,99,235,0.10)', color: 'primary.main'}}><SettingsIcon size={20} /></Box>
                            <Box>
                                <Typography variant="h6">Preferências</Typography>
                                <Typography variant="body2" color="text.secondary">Personalize sua experiência</Typography>
                            </Box>
                        </Stack>
                        <Stack spacing={2}>
                            <TextField select label="Idioma" defaultValue="pt-BR">
                                <MenuItem value="pt-BR">Português (Brasil)</MenuItem>
                                <MenuItem value="en-US">English (US)</MenuItem>
                                <MenuItem value="es">Español</MenuItem>
                            </TextField>
                            <TextField select label="Tema" defaultValue="claro">
                                <MenuItem value="claro">Claro</MenuItem>
                                <MenuItem value="escuro">Escuro</MenuItem>
                                <MenuItem value="auto">Automático</MenuItem>
                            </TextField>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </PortalShell>
    );
}
